import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "next-i18next";

import { getImageName, prettifyData } from "helpers";
import { useGlobalState } from "../../../../../context/GlobalStateContext";
import StoreLogo from "components/customImages/StoreLogo";
import { StoreDetail, StoreReviews } from "../../../../../helpers/types";
import RatingDetails from "../(commentSection)/RatingDetails";
import useIsDesktop from "app/custom_hooks/useIsDesktop";
import { FaThumbsUp, FaTruck, FaBolt, FaGlobe, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import { redirectToExternalPage } from "helpers/navigation";

const INFO_ICONS = {
  likes: <FaThumbsUp className="text-blue w-4 h-5" />,
  hasDelivery: <FaTruck className="text-blue w-4 h-5" />,
  answerSpeed: <FaBolt className="text-blue w-4 h-5" />,
};

const CONTACT_ICONS = {
  website: <FaGlobe className="text-blue w-4 h-5" />,
  phone: <FaPhone className="text-blue w-4 h-5" />,
  state: <FaMapMarkerAlt className="text-blue w-4 h-5" />,
};

const StoreOverViewInfo = ({ store = {} as StoreDetail, reviews = {} as StoreReviews }) => {
  const { language, direction } = useGlobalState();
  const { t: translate } = useTranslation("page_detail");

  function phoneFormatter(value: string, direction: string) {
    if (!value) {
      return "";
    }

    const formattedValue = value.startsWith("+216")
      ? value
      : direction === "rtl"
        ? `216+ ${value}`
        : `+216 ${value}`;

    if (direction === "rtl") {
      const [countryCode, ...rest] = formattedValue.split(" ");
      return `${rest.reverse().join(" ")} ${countryCode}`;
    }
    return formattedValue;
  }

  const isDesktop = useIsDesktop();
  const sideBorderClass = `pt-1 ${direction === "rtl" ? "border-r-[3px] pr-6" : "border-l-[3px] pl-2"} border-lightGrey flex flex-col gap-1`;

  function redirectToPage() {
    redirectToExternalPage(store?.url);
  }

  function displayRating(reviews) {
    const isArabic = language === "ar";
    if (isArabic) {
      if (reviews === 1) return translate("one_review");
      if (reviews === 2) return translate("two_reviews");
      const key = reviews <= 10 ? "reviews" : "review";
      return `${reviews} ${translate(key)}`;
    }
    const key =
      reviews === 1
        ? "one_review"
        : reviews === 2
          ? "two_reviews"
          : reviews <= 10
            ? "reviews"
            : "review";
    return `${reviews} ${translate(key)}`;
  }

  return (
    <div className="flex flex-col gap-3">
      {isDesktop && (
        <div className="flex flex-row gap-2">
          <div className="w-[15%] flex items-start justify-center">
            <div className="relative min-h-fit min-w-fit">
              {store?.plan === "STARTER" && (
                <div className="sm:w-8 lg:w-9 sm:h-8 lg:h-9 rounded-full absolute -top-1 -left-1">
                  <Image
                    className="rounded-full"
                    src={"/images/badge-icon.svg"}
                    alt="badge logo"
                    width={100}
                    height={100}
                  />
                </div>
              )}
              <div onClick={redirectToPage} className="cursor-pointer">
                <StoreLogo
                  key={store?.mainImage}
                  imageName={
                    store?.nameChanges?.[0]?.logo
                      ? getImageName(store?.nameChanges?.[0]?.logo)
                      : getImageName(store?.mainImage)
                  }
                  alt={translate("image_not_found")}
                  className="rounded-full object-cover border-[4px] border-orange-300 w-[60px] lg:w-[90px] aspect-square"
                />
              </div>

              <div className="w-6 h-6 rounded-full absolute bottom-0 right-1 bg-white p-[2.5px]">
                <Image
                  className="rounded-full"
                  src={
                    store?.url?.includes("instagram")
                      ? "/images/micro-icon-instagram.svg"
                      : store?.url?.includes("facebook")
                        ? "/images/micro-icon-facebook.svg"
                        : "/images/small-world.svg"
                  }
                  alt="delivery truck logo"
                  width={100}
                  height={100}
                />
              </div>
            </div>
          </div>
          {/* -------- Rating details --------- */}

          <div className="w-[85%] flex flex-col gap-1 justify-center">
            <h1
              className={`font-bold break-words pt-3 text-sm lg:text-lg ${direction === "rtl" ? "text-right" : "text-left"}`}
            >
              <span onClick={redirectToPage} className="cursor-pointer p-0 hover:underline">
                {store.name?.split("_").join(" ")}
              </span>
            </h1>
            {isDesktop && (
              <p
                className={`text-xl font-bold ${direction === "rtl" ? "text-right" : "text-left"}`}
              >
                {translate("rating_details")} ({displayRating(reviews?.totalReviews)})
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center pl-2 lg:pl-[7%]  gap-3 min-w-[100%]">
        <div className="flex flex-row w-full gap-3">
          {isDesktop && (
            <div className="w-[34%] lg:w-[50%] flex items-center">
              <RatingDetails reviews={reviews} />
            </div>
          )}

          <div className={`md:w-[30%] w-[50%] ${sideBorderClass}`}>
            {Object.keys(INFO_ICONS).map(
              (key, index) =>
                store[key] && (
                  <div key={`info__${index}`} className="flex items-center gap-2">
                    {INFO_ICONS[key]}
                    <p className="xs:text-xs sm:text-sm lg:text-sm">
                      {prettifyData(key, store[key], translate, language)}
                    </p>
                  </div>
                ),
            )}
          </div>

          <div className={`md:w-[30%] w-[50%] ${sideBorderClass}`}>
            {Object.keys(CONTACT_ICONS).map(
              (key, index) =>
                store[key] && (
                  <div key={`contact__${index}`} className="flex items-center gap-1">
                    {CONTACT_ICONS[key]}
                    {key === "state" ? (
                      <p className="xs:text-xs sm:text-sm lg:text-sm text-black">
                        {prettifyData(key, [store.country, store[key]], translate, language)}
                      </p>
                    ) : (
                      <Link
                        className="xs:text-xs sm:text-sm lg:text-sm text-blue break-all"
                        href={
                          key === "phone"
                            ? `tel:${store[key]}`
                            : store[key]?.startsWith("http://") ||
                                store[key]?.startsWith("https://")
                              ? store[key]
                              : `https://${store[key]}`
                        }
                        target={key === "phone" ? "" : "_blank"}
                      >
                        {key === "phone"
                          ? phoneFormatter(store[key], direction)
                          : store[key]?.length > (isDesktop ? 32 : 20)
                            ? store[key]?.slice(0, isDesktop ? 32 : 20) + "..."
                            : store[key]}{" "}
                      </Link>
                    )}
                  </div>
                ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreOverViewInfo;
